import { useMutation, useQuery } from '@apollo/client'
import {
  CreateSheetThreadCommentDocument,
  FetchUsersDocument,
  MarkSheetThreadCommentNotificationsAsReadDocument,
  type SheetThread,
  SheetThreadCommentNotificationsDocument,
  SheetThreadsDocument,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { commentDisplayName, ReadonlyMentionTextarea, useMe, UserAvatar } from '@salescore/client-common'
import { stableStringify } from '@salescore/features'
import { organizationHasFeature } from '@salescore/frontend-common'
import { App, Button, Form, List, Skeleton } from 'antd'
import dayjs from 'dayjs'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type KeyboardEventHandler, type MutableRefObject, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { z } from 'zod'

import { getBackgroundViewId } from '../../domain/miscSelectors'
import { kpiParameterAtom, sheetPickedPresetNameAtom } from '../../recoil/navigation/atoms'
import { additionalConfigAtom } from '../../recoil/records/atoms'
import { viewsContextAtom } from '../../recoil/view/atoms'
import { useViewConfigSheet } from '../../recoil/viewConfig'
import { CommentForm } from './CommentForm'

export const fieldTypeSchema = z.object({
  text: z.string(),
})

type FieldType = z.infer<typeof fieldTypeSchema>

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SheetThreadForm({
  commentRefs,
  recordId,
  recordName,
  sheetThread,
  open,
  onOpenChange,
  view,
}: {
  commentRefs: MutableRefObject<Map<string, HTMLElement>>
  recordId: string | undefined
  recordName: string | undefined
  sheetThread: SheetThread
  open: boolean
  onOpenChange: (open: boolean) => void
  view: ViewFieldsFragment
}) {
  const { organization } = useMe()
  const [createSheetThreadComment, result] = useMutation(CreateSheetThreadCommentDocument)
  const viewsContext = useRecoilValue(viewsContextAtom)
  const backgroundViewId = getBackgroundViewId(view.id, viewsContext.prevView)
  const config = useViewConfigSheet()
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = useState(false) // form.isSubmitting が使えるようになったらそちらに移行: https://github.com/ant-design/ant-design/issues/27667
  const users = useQuery(FetchUsersDocument, {
    variables: {
      organizationId: organization.id,
    },
    fetchPolicy: 'cache-first',
  })
  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const { kpiPivotPresetName } = useRecoilValue(viewsContextAtom)
  const [sheetPresetName] = useRecoilState(sheetPickedPresetNameAtom)
  const [markSheetThreadCommentNotificationsAsReadMutation] = useMutation(
    MarkSheetThreadCommentNotificationsAsReadDocument,
  )
  const sheetThreadCommentNotificationsQueryData = useQuery(SheetThreadCommentNotificationsDocument, {
    variables: {
      organizationId: organization.id,
    },
    // サイダーでフェッチしているため、ビュー内ではそのキャッシュを使う
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: より細かいキャッシュの制御が必要な場合は、fetchPolicy を変更する
    fetchPolicy: 'cache-first',
  })
  const { message } = App.useApp()
  const options =
    users.data?.users.map((user) => ({
      id: user.id,
      display: user.name,
    })) ?? []
  const comments = open ? sheetThread.comments : [sheetThread.comments[0]].filter(isNotNullish)
  async function updateNotifications() {
    await markSheetThreadCommentNotificationsAsReadMutation({
      variables: {
        commentIds: comments.filter(isNotNullish).map((comment) => comment.id),
        organizationId: organization.id,
      },
    })
    await sheetThreadCommentNotificationsQueryData.refetch()
  }

  useEffect(() => {
    void updateNotifications()
  }, [comments.length])

  useEffect(() => {
    // ここで createSheetThreadComment のエラーハンドリングをしている
    if (result.error?.message === undefined) {
      return
    }
    const errorJson = z
      .string()
      .refine((value) => {
        try {
          JSON.parse(value)
          return true
        } catch {
          return false
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value) => JSON.parse(value))
      .safeParse(result.error.message)
    if (!errorJson.success) {
      void message.error(result.error.message)
      return
    }
    const parsed = z
      .object({
        error: z.object({ code: z.literal('slack_webapi_platform_error'), data: z.object({ error: z.string() }) }),
        postMessageRequestBody: z.any(),
        channel: z.object({ id: z.string(), name: z.string() }),
      })
      .safeParse(errorJson.data)
    if (parsed.success) {
      if (parsed.data.error.data.error === 'channel_not_found' || parsed.data.error.data.error === 'not_in_channel') {
        void message.error(
          t(`コメントするには次の Slack チャネルに所属している必要があります。${stableStringify(parsed.data.channel)}`),
        )
        return
      }
      if (parsed.data.error.data.error === 'token_revoked') {
        void message.error(t('Slack トークンが失効しています。再連携をお試しください。'))
        return
      }
    }
    void message.error(result.error.message)
  }, [result.error?.message])

  const commentFeatureEnabled = organizationHasFeature(organization, 'enable_slack_comment')

  if (!commentFeatureEnabled || recordId === undefined || config.tree?.modelName === undefined) {
    return null
  }
  const { modelName } = config.tree
  const onFinish = async (fields: FieldType) => {
    if (isSubmitting) {
      return
    }
    try {
      const { text } = fieldTypeSchema.parse(fields)
      if (backgroundViewId === undefined) {
        return
      }
      setIsSubmitting(true)
      await createSheetThreadComment({
        variables: {
          sheetThreadCommentCreateInput: {
            viewId: backgroundViewId,
            organizationId: organization.id,
            threadId: sheetThread.id,
            recordId,
            modelName,
            recordName,
            message: text,
            sheetViewId: view.id,
            kpiPivotParameter,
            kpiPivotPresetName,
            sheetPresetName,
            additionalConfigFilterLeafs: additionalConfig.filterLeafs,
          },
        },
        refetchQueries: [
          {
            query: SheetThreadsDocument,
            variables: {
              organizationId: organization.id,
              recordIds: [recordId],
            },
            fetchPolicy: 'network-only',
          },
        ],
      })
    } catch {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: エラー処理
    } finally {
      setIsSubmitting(false)
    }
    form.resetFields()
    setIsSubmitting(false)
  }

  const handleKeyDown: KeyboardEventHandler<HTMLFormElement> = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      form.submit()
    }
  }

  return (
    <div className="w-full">
      <List>
        {comments.filter(isNotNullish).map((comment, index) => {
          const displayName = commentDisplayName(comment)
          return (
            <List.Item
              ref={(reference) => {
                if (reference === null) {
                  return
                }
                commentRefs.current.set(comment.id, reference)
              }}
              key={comment.id}
              className={`!border-none ${index === 0 ? '' : '!pl-4'}`}
            >
              <Skeleton avatar title={false} loading={result.loading} active>
                <div className="flex w-full flex-col">
                  <List.Item.Meta
                    avatar={<UserAvatar name={displayName} imageUrl={comment.createdBy?.imageUrl ?? ''} size="small" />}
                    title={
                      <div className="flex items-center justify-between break-all">
                        <span>{displayName}</span>
                        <span className="text-xs">{dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                      </div>
                    }
                    description={<ReadonlyMentionTextarea message={comment.message} options={options} />}
                  />
                  {!open && (
                    <div className="pl-8">
                      <Button
                        type="link"
                        size="small"
                        color="primary"
                        onClick={() => {
                          onOpenChange(true)
                        }}
                      >
                        {t(`{{count}} 件の返信`, { count: sheetThread.comments.length - 1 })}
                      </Button>
                    </div>
                  )}
                </div>
              </Skeleton>
            </List.Item>
          )
        })}
      </List>
      {open && (
        <div className="pl-4">
          <Form
            form={form}
            onFinish={(values) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              void onFinish(values)
            }}
            onKeyDown={handleKeyDown}
          >
            <CommentForm
              isReply={true}
              isSmall={true}
              isDisabled={isSubmitting}
              onClickCancelButton={() => {
                onOpenChange(false)
              }}
            />
          </Form>
        </div>
      )}
    </div>
  )
}
